import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, FileText, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const DEFAULT_TERMS = [
  'This quotation is indicative and subject to final site assessment and structural design.',
  '50% advance with work order, balance as per agreed payment schedule.',
  'Prices are valid for the period stated above and subject to revision thereafter.',
  'Transportation, local approvals and site electricity/water to client scope unless stated.',
].join('\n');

const inr = (n) => `\u20b9${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const makeItem = (overrides = {}) => ({
  uid: `qi-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
  description: '',
  qty: '',
  unit: 'sq.ft',
  rate: '',
  ...overrides,
});

export const QuotationComposer = ({ authAxios, lead, onSent }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [intro, setIntro] = useState(
    `Thank you for your interest in MELOPA INFRA. Based on your ${lead?.project_type?.toLowerCase() || 'project'} requirement, please find our quotation below.`
  );
  const [items, setItems] = useState([
    makeItem({ description: 'EPS Wall & Roof Panel System — supply and installation' }),
  ]);
  const [gstPercent, setGstPercent] = useState('18');
  const [validityDays, setValidityDays] = useState('15');
  const [terms, setTerms] = useState(DEFAULT_TERMS);

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, it) => sum + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
    const gst = subtotal * (Number(gstPercent) || 0) / 100;
    return { subtotal, gst, total: subtotal + gst };
  }, [items, gstPercent]);

  const validItems = items.filter((it) => it.description.trim() && Number(it.qty) > 0 && Number(it.rate) >= 0);
  const canSend = validItems.length > 0 && !sending;

  const setItem = (i, k, v) => {
    setItems((arr) => {
      const copy = [...arr];
      copy[i] = { ...copy[i], [k]: v };
      return copy;
    });
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await authAxios.post(`/admin/leads/${lead.id}/quotation/send`, {
        intro: intro.trim(),
        items: validItems.map((it) => ({
          description: it.description.trim(),
          qty: Number(it.qty),
          unit: it.unit.trim() || 'unit',
          rate: Number(it.rate),
        })),
        gst_percent: Number(gstPercent) || 0,
        validity_days: Number(validityDays) || 15,
        terms,
      }, { timeout: 90000 });
      toast.success(`Quotation ${res.data.quote_number} sent to ${lead.email}`);
      setOpen(false);
      if (onSent) onSent(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Quotation send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={!lead?.email}
        title={!lead?.email ? 'This lead did not provide an email address' : undefined}
        data-testid="admin-lead-quotation-button"
        onClick={() => setOpen(true)}
      >
        <FileText className="w-3.5 h-3.5 mr-1.5 text-[#155E6D]" /> Create Quotation
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="quotation-composer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Quotation — {lead?.name}
              <span className="block text-xs font-normal text-muted-foreground mt-1">
                Sends a branded itemised HTML quotation to {lead?.email}. Lead status moves to QUOTATION.
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium">Introduction</label>
              <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} className="mt-1.5 min-h-[60px] text-sm" data-testid="quotation-intro-input" />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Line Items</label>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="quotation-add-item-button"
                  onClick={() => setItems((a) => [...a, makeItem()])}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
                </Button>
              </div>
              <div className="mt-2 space-y-2">
                {items.map((it, i) => (
                  <div key={it.uid} className="grid grid-cols-[1fr_80px_80px_110px_36px] gap-2 items-center" data-testid={`quotation-item-${i}`}>
                    <Input placeholder="Description" value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} className="h-10 text-sm" data-testid={`quotation-item-description-${i}`} />
                    <Input placeholder="Qty" type="number" value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} className="h-10 text-sm" data-testid={`quotation-item-qty-${i}`} />
                    <Input placeholder="Unit" value={it.unit} onChange={(e) => setItem(i, 'unit', e.target.value)} className="h-10 text-sm" />
                    <Input placeholder={'Rate \u20b9'} type="number" value={it.rate} onChange={(e) => setItem(i, 'rate', e.target.value)} className="h-10 text-sm" data-testid={`quotation-item-rate-${i}`} />
                    <button
                      type="button"
                      aria-label="Remove item"
                      className="h-10 w-9 rounded-md border border-border flex items-center justify-center text-destructive hover:bg-muted disabled:opacity-30"
                      disabled={items.length === 1}
                      onClick={() => setItems((a) => a.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">GST %</label>
                <Input type="number" value={gstPercent} onChange={(e) => setGstPercent(e.target.value)} className="h-10 mt-1.5" data-testid="quotation-gst-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Validity (days)</label>
                <Input type="number" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} className="h-10 mt-1.5" data-testid="quotation-validity-input" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Terms &amp; Conditions <span className="text-muted-foreground font-normal">(one per line)</span></label>
              <Textarea value={terms} onChange={(e) => setTerms(e.target.value)} className="mt-1.5 min-h-[100px] text-sm" data-testid="quotation-terms-input" />
            </div>

            <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm space-y-1.5" data-testid="quotation-totals">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{inr(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST ({gstPercent || 0}%)</span><span>{inr(totals.gst)}</span></div>
              <div className="flex justify-between font-semibold text-base pt-1.5 border-t border-border"><span>Total</span><span className="text-[#155E6D]">{inr(totals.total)}</span></div>
            </div>

            <Button className="w-full h-11" disabled={!canSend} onClick={send} data-testid="quotation-send-button">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Quotation to {lead?.email}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
